import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material'
import {
  Save,
  Cancel,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Assignment
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import closureService from '../../../services/closureService'
import mrdService from '../../../services/mrdservice'

const ClosureForm = ({ fileId, onSuccess, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [mrdFile, setMrdFile] = useState(null)
  const [checklist, setChecklist] = useState([])
  const [formData, setFormData] = useState({
    closureType: 'NORMAL_CLOSURE',
    closureReason: '',
    finalDiagnosis: '',
    treatmentSummary: '',
    followUpRequired: false,
    followUpInstructions: '',
    documentsComplete: false,
    codingComplete: false,
    billingComplete: false,
    remarks: ''
  })

  useEffect(() => {
    if (fileId) {
      loadFileData()
      loadClosureChecklist()
    }
  }, [fileId])

  const loadFileData = async () => {
    try {
      const response = await mrdService.getFileById(fileId)
      if (response.data.success) {
        setMrdFile(response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load file data', { variant: 'error' })
    }
  }

  const loadClosureChecklist = async () => {
    try {
      const response = await closureService.getVerificationChecklist(fileId)
      if (response.data.success) {
        const checklistData = response.data.data.checklist || {}
        // Convert checklist object to array format
        const checklistArray = Object.entries(checklistData).map(([key, value]) => ({
          _id: key,
          description: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
          completed: value,
          mandatory: true // All items are mandatory for closure
        }))
        setChecklist(checklistArray)
      }
    } catch (error) {
      console.log('Checklist not available:', error)
    }
  }

  const handleChecklistUpdate = (itemId, completed) => {
    // Only update local state, don't call API until final submission
    setChecklist(prev => 
      prev.map(item => 
        item._id === itemId ? { ...item, completed } : item
      )
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.closureReason) {
      enqueueSnackbar('Please enter closure reason', { variant: 'error' })
      return
    }

    // Check if all mandatory checklist items are completed
    const mandatoryIncomplete = checklist.filter(item => 
      item.mandatory && !item.completed
    )
    
    if (mandatoryIncomplete.length > 0) {
      enqueueSnackbar('Please complete all mandatory checklist items', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      // Prepare closure data
      const closureData = {
        closureReason: formData.closureReason,
        verificationChecklist: checklist.reduce((acc, item) => {
          acc[item._id] = item.completed
          return acc
        }, {}),
        remarks: formData.remarks
      }
      
      const response = await closureService.closeFile(fileId, closureData)
      
      if (response.data.success) {
        enqueueSnackbar('File closed successfully', { variant: 'success' })
        if (onSuccess) onSuccess(response.data.data)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to close file'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const closureTypes = [
    { value: 'NORMAL_CLOSURE', label: 'Normal Closure' },
    { value: 'DISCHARGE_CLOSURE', label: 'Discharge Closure' },
    { value: 'TRANSFER_CLOSURE', label: 'Transfer Closure' },
    { value: 'DEATH_CLOSURE', label: 'Death Closure' },
    { value: 'ADMINISTRATIVE_CLOSURE', label: 'Administrative Closure' }
  ]

  const getChecklistIcon = (item) => {
    if (item.completed) {
      return <CheckCircle color="success" />
    } else if (item.mandatory) {
      return <ErrorIcon color="error" />
    } else {
      return <Warning color="warning" />
    }
  }

  const completedItems = checklist.filter(item => item.completed).length
  const totalItems = checklist.length
  const completionPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* File Information */}
        {mrdFile && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="File Information" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      File Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {mrdFile.fileNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Patient Name
                    </Typography>
                    <Typography variant="body1">
                      {mrdFile.patientName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Admission Date
                    </Typography>
                    <Typography variant="body1">
                      {mrdFile.admissionDate ? format(new Date(mrdFile.admissionDate), 'MMM dd, yyyy') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Status
                    </Typography>
                    <Chip
                      label={mrdFile.status}
                      color={mrdFile.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Closure Checklist */}
        {checklist.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Closure Checklist"
                subheader={`${completedItems}/${totalItems} items completed (${completionPercentage.toFixed(0)}%)`}
                action={
                  <Chip
                    label={`${completionPercentage.toFixed(0)}%`}
                    color={completionPercentage === 100 ? 'success' : 'warning'}
                  />
                }
              />
              <CardContent>
                <List dense>
                  {checklist.map((item, index) => (
                    <React.Fragment key={item._id}>
                      <ListItem>
                        <ListItemIcon>
                          {getChecklistIcon(item)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.completed}
                                  onChange={(e) => handleChecklistUpdate(item._id, e.target.checked)}
                                />
                              }
                              label={item.description}
                            />
                          }
                          secondary={item.mandatory ? 'Mandatory' : 'Optional'}
                        />
                      </ListItem>
                      {index < checklist.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Closure Details */}
        <Grid item xs={12} md={checklist.length > 0 ? 6 : 12}>
          <Card>
            <CardHeader title="Closure Details" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Closure Type</InputLabel>
                    <Select
                      value={formData.closureType}
                      label="Closure Type"
                      onChange={(e) => setFormData(prev => ({ ...prev, closureType: e.target.value }))}
                    >
                      {closureTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Closure Reason *"
                    value={formData.closureReason}
                    onChange={(e) => setFormData(prev => ({ ...prev, closureReason: e.target.value }))}
                    placeholder="Enter reason for closure..."
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Final Diagnosis"
                    value={formData.finalDiagnosis}
                    onChange={(e) => setFormData(prev => ({ ...prev, finalDiagnosis: e.target.value }))}
                    placeholder="Enter final diagnosis..."
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Treatment Summary"
                    value={formData.treatmentSummary}
                    onChange={(e) => setFormData(prev => ({ ...prev, treatmentSummary: e.target.value }))}
                    placeholder="Enter treatment summary..."
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.followUpRequired}
                        onChange={(e) => setFormData(prev => ({ ...prev, followUpRequired: e.target.checked }))}
                      />
                    }
                    label="Follow-up Required"
                  />
                </Grid>

                {formData.followUpRequired && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Follow-up Instructions"
                      value={formData.followUpInstructions}
                      onChange={(e) => setFormData(prev => ({ ...prev, followUpInstructions: e.target.value }))}
                      placeholder="Enter follow-up instructions..."
                      multiline
                      rows={2}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Completion Status
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.documentsComplete}
                          onChange={(e) => setFormData(prev => ({ ...prev, documentsComplete: e.target.checked }))}
                        />
                      }
                      label="All documents are complete"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.codingComplete}
                          onChange={(e) => setFormData(prev => ({ ...prev, codingComplete: e.target.checked }))}
                        />
                      }
                      label="Medical coding is complete"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.billingComplete}
                          onChange={(e) => setFormData(prev => ({ ...prev, billingComplete: e.target.checked }))}
                        />
                      }
                      label="Billing is complete"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Additional remarks..."
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Validation Alerts */}
      {checklist.length > 0 && completionPercentage < 100 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            Please complete all mandatory checklist items before closing the file.
          </Typography>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={<Save />}
        >
          Close File
        </Button>
      </Box>
    </Box>
  )
}

export default ClosureForm